import {
  ChangeDetectionStrategy,
  Component,
  signal,
  HostListener
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ReactiveFormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AppService } from '@app/app.service';
import { AuthService } from '@app/auth/auth.service';
import { Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { LegalAcceptanceDialogComponent } from '@app/legal/legal-acceptance-dialog/legal-acceptance-dialog.component';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    RouterModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatProgressSpinner,
    MatDialogModule
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {

  // Enabling by default, to avoid delays in UI display
  isUserLogInEnabled = signal(true);
  loadingSettings = signal(true);

  hide = signal(true); // Toggle password visibility
  passwordMinLength = 5;
  form: FormGroup;

  loading = signal(false); // Loading progress bar

  userEmail: string | null = null;

  isMobile = window.innerWidth < 768;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private appService: AppService,
    private router: Router,
    private dialog: MatDialog
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });
  }

  ngOnInit() {
    document.body.classList.add('white-bg');
    this.getSystemGlobalSettings();
  }

  ngOnDestroy() {
    document.body.classList.remove('white-bg');
  }

  storeEmail() {
    this.userEmail = this.form.get('email')?.value;
  }

  login() {
    if (this.form.invalid) {
      return;
    }

    this.loading.set(true);

    this.authService.login(this.form.value).subscribe({
      next: (response) => {

        const token = response.data.token;
        const user = response.data.user;

        this.authService.setToken(token);
        
        // If the user needs to accept legal documents, 
        // we set the pending legal acceptance flag and open the dialog
        if (response.data.requires_legal_acceptance) {
          this.authService.setPendingLegalAcceptance(true);
          this.openLegalAcceptanceDialog(response.data.legal);
          this.loading.set(false);
          return;
        }

        this.loading.set(false);
        this.authService.setPendingLegalAcceptance(false);
        this.authService.setUserToStorage(user);
        this.authService.navigateToDefaultUrl();
      },

      error: (error) => {
        this.appService.handleError(error);
        this.loading.set(false);
      },
    });
  }

  private openLegalAcceptanceDialog(legal: any): void {
    const dialogRef = this.dialog.open(LegalAcceptanceDialogComponent, {
      width: '600px',
      disableClose: true,
      data: legal,
    });

    dialogRef.afterClosed().subscribe((accepted) => {
      if (accepted) {
        this.authService.navigateToDefaultUrl();
      }
    });
  }

  getSystemGlobalSettings(): void {

    this.loadingSettings.set(true);

    this.appService.getSystemGlobalSettings().subscribe({
      next: (response) => {
        const settings = response.data;

        this.isUserLogInEnabled.set(settings.login_enabled);
        this.loadingSettings.set(false);
      },
      error: (error) => {
        this.appService.handleError(error);
        this.loadingSettings.set(false);
      }
    });
  }
  
}